import { useMemo, useState } from "react";
import { GradientText, Title } from "lola-framework-ui-test";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/globalState";
import { FlagCircle } from "../FlagCircle";
import type { DestinationOption } from "../../utils/catalogCountries";
import { countryDisplayName } from "../../utils/country-display";
import { ScrollFadeIndicator } from "../ScrollFadeIndicator";
import { CloseLight } from "../../assets/navIcons";


export interface SelectCountrySlotProps {
  titleKey: string;
  countries: DestinationOption[];
  selectedCountry?: string | null;
  selectedCurrency?: string | null;
  onSelect: (countryCode: string, currencyCode: string) => void;
  onClose: () => void;
}

export const SelectCountrySlot: React.FC<SelectCountrySlotProps> = ({
  titleKey,
  countries,
  selectedCountry,
  selectedCurrency,
  onSelect,
  onClose,
}) => {
  const { t, i18n } = useTranslation("request");
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn } = colorConfig;
  const locale = i18n.language || "en";

  const [query, setQuery] = useState("");

  const options = useMemo(
    () =>
      countries
        .filter((c) => c.effectiveStatus === "AVAILABLE")
        .map((c) => ({
          ...c,
          countryName: countryDisplayName(c.countryCode, locale) || c.countryName,
        }))
        .slice()
        .sort((a, b) => {
          const byName = a.countryName.localeCompare(b.countryName);
          if (byName !== 0) return byName;
          return a.currencyCode.localeCompare(b.currencyCode);
        }),
    [countries, locale]
  );

  const normalize = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return options;
    return options.filter((c) =>
      [c.countryName, c.countryCode, c.currencyCode].some((field) =>
        normalize(field).includes(q)
      )
    );
  }, [options, query]);

  const handleSelect = (countryCode: string, currencyCode: string) => {
    onSelect(countryCode, currencyCode);
    onClose();
  };

  return (
    <section className="select-payout-slot">
      <header className="payout-details-header">
        <span className="payout-header-action" aria-hidden="true" />
        <Title title={t(titleKey)} align="center" color={bodyCopy} />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t("common.cancel")}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>
      <input
        type="text"
        className="select-country-search"
        placeholder={t("transfer.searchCountry")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={t("transfer.searchCountry")}
        autoComplete="off"
      />
      <ul className="select-payout-list">
        <ScrollFadeIndicator color={bodyCopy} />
        {filtered.length === 0 ? (
          <li className="select-country-empty">
            <GradientText textColor={bodyCopy}>
              {t("transfer.noCountriesFound")}
            </GradientText>
          </li>
        ) : null}
        {filtered.map((country) => {
          const isSelected =
            selectedCountry === country.countryCode &&
            selectedCurrency === country.currencyCode;
          return (
            <li
              key={`${country.countryCode}-${country.currencyCode}`}
              className={`select-payout-option ${
                isSelected ? "selected" : ""
              }`.trim()}
              onClick={() =>
                handleSelect(country.countryCode, country.currencyCode)
              }
              style={
                {
                  "--bgc": backgroundBtn,
                } as React.CSSProperties
              }
            >
              <section className="select-payout-icon">
                <FlagCircle iso2={country.countryCode.toLowerCase()} size={24} />
              </section>
              <GradientText
                textColor={bodyCopy}
                className="select-payout-label"
              >
                {country.countryName}
              </GradientText>
              <GradientText
                textColor={bodyCopy}
                className="select-payout-currency"
              >
                {country.currencyCode}
              </GradientText>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
